import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Kanban, 
  Calendar,
  User,
  Clock,
  AlertCircle,
  CheckCircle2,
  Pause,
  Play,
  Edit,
  Trash2
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

const Projects: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [searchTerm, setSearchTerm] = useState('');

  const mockProjects = [
    {
      id: '1',
      name: 'React Learning Path',
      description: 'Complete React.js mastery course with practical projects',
      status: 'in-progress' as const,
      priority: 'high' as const,
      assignee: 'You',
      dueDate: '2025-02-15',
      progress: 65,
      tasks: 12,
      completedTasks: 8,
      tags: ['React', 'Frontend', 'Learning']
    },
    {
      id: '2',
      name: 'Machine Learning Research',
      description: 'Deep dive into neural networks and deep learning algorithms',
      status: 'planning' as const,
      priority: 'medium' as const,
      assignee: 'You',
      dueDate: '2025-03-01',
      progress: 20,
      tasks: 15,
      completedTasks: 3,
      tags: ['ML', 'Research', 'Python']
    },
    {
      id: '3',
      name: 'Database Optimization',
      description: 'Optimize database queries and improve performance',
      status: 'completed' as const,
      priority: 'low' as const,
      assignee: 'You',
      dueDate: '2025-01-20',
      progress: 100,
      tasks: 8,
      completedTasks: 8,
      tags: ['Database', 'Performance']
    },
    {
      id: '4',
      name: 'UI/UX Portfolio',
      description: 'Create a comprehensive design portfolio',
      status: 'on-hold' as const,
      priority: 'urgent' as const,
      assignee: 'You',
      dueDate: '2025-02-28',
      progress: 40,
      tasks: 10,
      completedTasks: 4,
      tags: ['Design', 'Portfolio']
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planning': return <Clock className="w-4 h-4" />;
      case 'in-progress': return <Play className="w-4 h-4" />;
      case 'completed': return <CheckCircle2 className="w-4 h-4" />;
      case 'on-hold': return <Pause className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return colors.blue;
      case 'in-progress': return colors.yellow;
      case 'completed': return colors.green;
      case 'on-hold': return colors.red;
      default: return colors.subtext0;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return colors.red;
      case 'high': return colors.peach;
      case 'medium': return colors.yellow;
      case 'low': return colors.green;
      default: return colors.blue;
    }
  };

  const handleEditProject = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteProject = (projectId: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      console.log('Delete project:', projectId);
      // Handle delete logic here
    }
  };

  const ProjectCard: React.FC<{ project: typeof mockProjects[0] }> = ({ project }) => (
    <div
      className="p-6 rounded-xl border transition-all hover:scale-105 cursor-pointer"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
      onClick={() => handleEditProject(project.id)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="p-2 rounded-lg"
            style={{ backgroundColor: colors.surface0 }}
          >
            <Kanban className="w-5 h-5" style={{ color: colors.blue }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: colors.text }}>
              {project.name}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <div 
                className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                style={{ 
                  backgroundColor: colors.surface0,
                  color: getStatusColor(project.status)
                }}
              >
                {getStatusIcon(project.status)}
                {project.status.replace('-', ' ')}
              </div>
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: getPriorityColor(project.priority) }}
              />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleEditProject(project.id);
            }}
            className="p-1 rounded hover:opacity-80"
          >
            <Edit className="w-4 h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteProject(project.id);
            }}
            className="p-1 rounded hover:opacity-80"
          >
            <Trash2 className="w-4 h-4" style={{ color: colors.red }} />
          </button>
        </div>
      </div>

      <p className="text-sm mb-4 line-clamp-2" style={{ color: colors.subtext0 }}>
        {project.description}
      </p>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm" style={{ color: colors.text }}>Progress</span>
          <span className="text-sm" style={{ color: colors.subtext0 }}>
            {project.completedTasks}/{project.tasks} tasks
          </span>
        </div>
        <div 
          className="w-full h-2 rounded-full"
          style={{ backgroundColor: colors.surface0 }}
        >
          <div 
            className="h-2 rounded-full transition-all"
            style={{ 
              backgroundColor: colors.blue,
              width: `${project.progress}%`
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {project.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.text
              }}
            >
              {tag}
            </span>
          ))}
        </div>
        
        <div className="flex items-center gap-2 text-xs" style={{ color: colors.subtext0 }}>
          <Calendar className="w-3 h-3" />
          {new Date(project.dueDate).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  const statusColumns = [
    { key: 'planning', title: 'Planning', color: colors.blue },
    { key: 'in-progress', title: 'In Progress', color: colors.yellow },
    { key: 'completed', title: 'Completed', color: colors.green },
    { key: 'on-hold', title: 'On Hold', color: colors.red }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Projects
        </h1>
        
        <button 
          onClick={() => navigate('/projects/new')}
          className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
          style={{ backgroundColor: colors.blue }}
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: colors.subtext0 }}
            />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            className="px-4 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <Filter className="w-4 h-4" />
            Filter
          </button>
          
          <div 
            className="flex items-center rounded-lg border"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-2 rounded-l-lg transition-colors ${
                viewMode === 'board' ? 'text-white' : ''
              }`}
              style={{
                backgroundColor: viewMode === 'board' ? colors.blue : 'transparent',
                color: viewMode === 'board' ? 'white' : colors.text
              }}
            >
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-r-lg transition-colors ${
                viewMode === 'list' ? 'text-white' : ''
              }`}
              style={{
                backgroundColor: viewMode === 'list' ? colors.blue : 'transparent',
                color: viewMode === 'list' ? 'white' : colors.text
              }}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Projects Display */}
      {viewMode === 'board' ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {statusColumns.map((column) => (
            <div key={column.key} className="space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: column.color }}
                />
                <h3 className="font-semibold" style={{ color: colors.text }}>
                  {column.title}
                </h3>
                <span 
                  className="text-sm px-2 py-1 rounded"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.subtext0
                  }}
                >
                  {mockProjects.filter(p => p.status === column.key).length}
                </span>
              </div>
              
              <div className="space-y-4">
                {mockProjects
                  .filter(project => project.status === column.key)
                  .map((project) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {mockProjects.map((project) => (
            <div
              key={project.id}
              className="p-6 rounded-xl border transition-all hover:opacity-80 cursor-pointer"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0
              }}
              onClick={() => handleEditProject(project.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
                      {project.name}
                    </h3>
                    <div 
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                      style={{ 
                        backgroundColor: colors.surface0,
                        color: getStatusColor(project.status)
                      }}
                    >
                      {getStatusIcon(project.status)}
                      {project.status.replace('-', ' ')}
                    </div>
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getPriorityColor(project.priority) }}
                    />
                  </div>
                  
                  <p className="text-sm mb-4" style={{ color: colors.subtext0 }}>
                    {project.description}
                  </p>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-full h-2 rounded-full bg-gray-200"
                        style={{ backgroundColor: colors.surface0, width: '100px' }}
                      >
                        <div 
                          className="h-2 rounded-full transition-all"
                          style={{ 
                            backgroundColor: colors.blue,
                            width: `${project.progress}%`
                          }}
                        />
                      </div>
                      <span className="text-sm" style={{ color: colors.subtext0 }}>
                        {project.progress}%
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm" style={{ color: colors.subtext0 }}>
                      <User className="w-4 h-4" />
                      {project.assignee}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm" style={{ color: colors.subtext0 }}>
                      <Calendar className="w-4 h-4" />
                      {new Date(project.dueDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProject(project.id);
                    }}
                    className="p-2 rounded hover:opacity-80"
                  >
                    <Edit className="w-4 h-4" style={{ color: colors.subtext0 }} />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                    className="p-2 rounded hover:opacity-80"
                  >
                    <Trash2 className="w-4 h-4" style={{ color: colors.red }} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;